import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Landmark, RefreshCw, Unplug, ArrowDownToLine, Building2, Wallet, CreditCard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import { useFreemium } from "@/hooks/useFreemium";

interface BankAccount {
  id: string;
  institution_name: string;
  account_name: string | null;
  account_type: string | null;
  account_subtype: string | null;
  account_mask: string | null;
  current_balance: number;
  available_balance: number;
  currency_code: string;
  last_synced_at: string | null;
  is_active: boolean;
}

interface BankTransaction {
  id: string;
  bank_account_id: string;
  plaid_transaction_id: string;
  amount: number;
  date: string;
  name: string | null;
  merchant_name: string | null;
  category: string | null;
  pending: boolean;
  imported: boolean;
  finance_record_id: string | null;
}

export default function BankingTab() {
  const queryClient = useQueryClient();
  const { isPreview, requireSubscription } = useFreemium();
  const [disconnectId, setDisconnectId] = useState<string | null>(null);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [selectedTxs, setSelectedTxs] = useState<Set<string>>(new Set());
  const [plaidNotConfigured, setPlaidNotConfigured] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Fetch connected accounts from DB directly
  const { data: accounts = [], isLoading: loadingAccounts } = useQuery({
    queryKey: ["connected_bank_accounts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("connected_bank_accounts")
        .select("id, institution_name, account_name, account_type, account_subtype, account_mask, current_balance, available_balance, currency_code, last_synced_at, is_active")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as BankAccount[];
    },
  });

  // Fetch bank transactions
  const { data: transactions = [], isLoading: loadingTxs } = useQuery({
    queryKey: ["bank_transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bank_transactions")
        .select("*")
        .order("date", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as BankTransaction[];
    },
  });

  const handleConnectBank = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("plaid-create-link-token");
      if (error) throw error;
      if (data?.error) {
        if (data.error.includes("not configured")) {
          setPlaidNotConfigured(true);
          return;
        }
        throw new Error(data.error);
      }

      // Load Plaid Link
      const script = document.createElement("script");
      script.src = "https://cdn.plaid.com/link/v2/stable/link-initialize.js";
      script.onload = () => {
        const handler = (window as any).Plaid.create({
          token: data.link_token,
          onSuccess: async (publicToken: string, metadata: any) => {
            try {
              const { data: exchangeData, error: exchangeError } = await supabase.functions.invoke(
                "plaid-exchange-token",
                {
                  body: {
                    public_token: publicToken,
                    institution: metadata.institution,
                  },
                }
              );
              if (exchangeError) throw exchangeError;
              if (exchangeData?.error) throw new Error(exchangeData.error);
              
              queryClient.invalidateQueries({ queryKey: ["connected_bank_accounts"] });
            } catch (err: any) {
              toast.error(err.message || "Failed to connect bank");
            }
          },
          onExit: () => {},
        });
        handler.open();
      };
      document.head.appendChild(script);
    } catch (err: any) {
      toast.error(err.message || "Failed to initialize bank connection");
    }
  }, [queryClient]);

  const syncTransactions = useCallback(async (accountId: string) => {
    setSyncing(accountId);
    try {
      const { data, error } = await supabase.functions.invoke("plaid-sync-transactions", {
        body: { bank_account_id: accountId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      queryClient.invalidateQueries({ queryKey: ["bank_transactions"] });
      queryClient.invalidateQueries({ queryKey: ["connected_bank_accounts"] });
    } catch (err: any) {
      toast.error(err.message || "Sync failed");
    } finally {
      setSyncing(null);
    }
  }, [queryClient]);

  const disconnectMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("connected_bank_accounts")
        .update({ is_active: false })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["connected_bank_accounts"] });
      
      setDisconnectId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const importMutation = useMutation({
    mutationFn: async (txIds: string[]) => {
      const toImport = transactions.filter((t) => txIds.includes(t.id) && !t.imported);
      for (const tx of toImport) {
        const isExpense = tx.amount > 0; // Plaid: positive = money out
        const { data: inserted, error: insertError } = await supabase
          .from("finance_records")
          .insert({
            amount: Math.abs(tx.amount),
            category: tx.category || "Uncategorized",
            date: tx.date,
            member_name: tx.merchant_name || tx.name || null,
            notes: `Imported from bank: ${tx.name || ""}`,
            record_type: isExpense ? "expense" : "income",
          })
          .select("id")
          .single();

        if (!insertError && inserted) {
          await supabase
            .from("bank_transactions")
            .update({ imported: true, finance_record_id: inserted.id })
            .eq("id", tx.id);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bank_transactions"] });
      queryClient.invalidateQueries({ queryKey: ["finance_records"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard_finance"] });
      
      setSelectedTxs(new Set());
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const toggleTx = (id: string) => {
    setSelectedTxs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllUnimported = () => {
    const unimported = transactions.filter((t) => !t.imported).map((t) => t.id);
    setSelectedTxs(new Set(unimported));
  };

  const formatCurrency = (val: number) =>
    `$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  const totalBalance = accounts.reduce((sum, a) => sum + (a.current_balance || 0), 0);

  return (
    <div className="space-y-6">
      {/* Plaid not configured banner */}
      {plaidNotConfigured && (
        <Card className="border-accent/50 bg-accent/5">
          <CardContent className="flex items-center gap-3 py-4">
            <AlertCircle className="w-5 h-5 text-accent shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Plaid API keys not configured</p>
              <p className="text-xs text-muted-foreground">
                To connect bank accounts, please provide your Plaid Client ID and Secret. Contact your administrator to set these up.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary & Connect */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-foreground">Banking</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Connect bank accounts, view balances, and import transactions
          </p>
        </div>
        <Button
          size="sm"
          className="h-9 px-3 shrink-0"
          onClick={() => {
            if (!requireSubscription()) return;
            setComingSoonOpen(true);
          }}
        >
          {isPreview && <span className="mr-2"></span>}
          <Landmark className="w-4 h-4 mr-2" />
          Connect Bank
        </Button>
      </div>

      {/* Balance Cards */}
      {accounts.length > 0 && (
        <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {/* Total Balance */}
          <Card className="col-span-full sm:col-span-1">
            <CardHeader className="pb-2">
              <CardDescription>Total Balance</CardDescription>
              <CardTitle className="text-2xl">{formatCurrency(totalBalance)}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">
                {accounts.length} account{accounts.length !== 1 ? "s" : ""} connected
              </p>
            </CardContent>
          </Card>

          {/* Individual accounts */}
          {accounts.map((acc) => (
            <Card key={acc.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <CardDescription>{acc.institution_name}</CardDescription>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {acc.account_type || "Account"}
                  </Badge>
                </div>
                <CardTitle className="text-lg">
                  {acc.account_name || `••••${acc.account_mask || ""}`}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Current</p>
                    <p className="text-sm font-semibold">{formatCurrency(acc.current_balance)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Available</p>
                    <p className="text-sm font-semibold">{formatCurrency(acc.available_balance)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (!requireSubscription()) return;
                      syncTransactions(acc.id);
                    }}
                    disabled={syncing === acc.id}
                  >
                    <RefreshCw className={`w-3 h-3 mr-1 ${syncing === acc.id ? "animate-spin" : ""}`} />
                    Sync
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!requireSubscription()) return;
                      setDisconnectId(acc.id);
                    }}
                    className="text-destructive hover:text-destructive"
                  >
                    <Unplug className="w-3 h-3 mr-1" />
                    Disconnect
                  </Button>
                </div>
                {acc.last_synced_at && (
                  <p className="text-xs text-muted-foreground">
                    Last synced: {format(new Date(acc.last_synced_at), "MMM d, h:mm a")}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* No accounts state */}
      {!loadingAccounts && accounts.length === 0 && !plaidNotConfigured && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Wallet className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-1">No bank accounts connected</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Connect your church's bank account to automatically import transactions and view balances.
            </p>
            <Button onClick={() => {
              if (!requireSubscription()) return;
              setComingSoonOpen(true);
            }}>
              {isPreview && <span className="mr-2"></span>}
              <Landmark className="w-4 h-4 mr-2" />
              Connect Your First Bank
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Transactions */}
      {accounts.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Bank Transactions</CardTitle>
                <CardDescription>
                  {transactions.filter((t) => !t.imported).length} unimported transaction
                  {transactions.filter((t) => !t.imported).length !== 1 ? "s" : ""}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {selectedTxs.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!requireSubscription()) return;
                      importMutation.mutate(Array.from(selectedTxs));
                    }}
                    disabled={importMutation.isPending}
                  >
                    <ArrowDownToLine className="w-3 h-3 mr-1" />
                    Import {selectedTxs.size} Selected
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!requireSubscription()) return;
                    selectAllUnimported();
                  }}
                >
                  Select All Unimported
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loadingTxs ? (
              <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No transactions yet. Click "Sync" on an account to pull recent transactions.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <th className="w-10 px-4 py-3"></th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Description</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase px-4 py-3">Category</th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase px-4 py-3">Amount</th>
                      <th className="text-center text-xs font-medium text-muted-foreground uppercase px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx) => (
                      <tr key={tx.id} className="border-b border-border/50 hover:bg-muted/40">
                        <td className="px-4 py-2">
                          {!tx.imported && (
                            <Checkbox
                              checked={selectedTxs.has(tx.id)}
                              onCheckedChange={() => toggleTx(tx.id)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-foreground whitespace-nowrap">
                          {format(new Date(tx.date + "T00:00:00"), "MMM d, yyyy")}
                        </td>
                        <td className="px-4 py-2">
                          <p className="text-sm text-foreground">{tx.name || "—"}</p>
                          {tx.merchant_name && tx.merchant_name !== tx.name && (
                            <p className="text-xs text-muted-foreground">{tx.merchant_name}</p>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant="secondary" className="text-xs">
                            {tx.category || "Uncategorized"}
                          </Badge>
                        </td>
                        <td className={`px-4 py-2 text-sm font-semibold text-right whitespace-nowrap ${tx.amount > 0 ? "text-destructive" : "text-success"}`}>
                          {tx.amount > 0 ? "-" : "+"}
                          {formatCurrency(tx.amount)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {tx.pending ? (
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          ) : tx.imported ? (
                            <Badge className="text-xs bg-success/10 text-success border-0">Imported</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">New</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Disconnect Confirmation */}
      <AlertDialog open={!!disconnectId} onOpenChange={() => setDisconnectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect this bank account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will stop syncing transactions from this account. Existing imported records will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!requireSubscription()) return;
                if (disconnectId) disconnectMutation.mutate(disconnectId);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Connect bank coming soon */}
      <AlertDialog open={comingSoonOpen} onOpenChange={setComingSoonOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Connect Bank Coming Soon</AlertDialogTitle>
            <AlertDialogDescription>
              This feature will be available soon. You will be able to connect your bank account in an upcoming release.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setComingSoonOpen(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
